import React, { useState, useMemo } from 'react';
import SortableTable from '../SortableTable';
import GIQuestionHeader from './GIQuestionHeader';
import { safeParseDate } from '../../utils/dateUtils';

const GIChoiceQuestion = ({ questionId, questionText, questionOptions, answers, isCollapsed = false, onToggleCollapse }) => {
  const [activeTab, setActiveTab] = useState('summary');

  // Préparer les données des réponses de choix
  const choiceData = useMemo(() => {
    if (!answers || !answers.length || !questionOptions) {
      return { choicesCount: {}, tableData: [], totalResponses: 0, isMultipleChoice: false };
    }

    const choicesCount = {};
    const tableData = [];
    let isMultipleChoice = false;

    // Initialiser le compteur pour chaque option
    questionOptions.forEach((option, index) => {
      const optionId = option.id !== undefined ? option.id.toString() : index.toString();
      choicesCount[optionId] = {
        id: optionId,
        text: option.text,
        count: 0
      };
    });

    // Compter les réponses et préparer les données du tableau
    answers.forEach(answer => {
      if (answer.Answers[questionId]) {
        const answerValue = answer.Answers[questionId];

        // Vérifier si la valeur n'est pas null ou undefined
        if (answerValue === null || answerValue === undefined) {
          return; // Passer à la réponse suivante
        }

        // Vérifier si c'est un array (choix multiple)
        const isArray = Array.isArray(answerValue);
        if (isArray) {
          isMultipleChoice = true;
        }

        // Normaliser en array pour un traitement uniforme - filtrer les valeurs null/undefined
        const selectedChoiceIds = isArray
          ? answerValue.filter(id => id !== null && id !== undefined).map(id => id.toString())
          : [answerValue.toString()];

        // Si après filtrage on n'a aucun choix valide, passer à la réponse suivante
        if (selectedChoiceIds.length === 0) {
          return;
        }

        // Incrémenter le compteur pour chaque choix sélectionné
        selectedChoiceIds.forEach(selectedChoiceId => {
          if (choicesCount[selectedChoiceId]) {
            choicesCount[selectedChoiceId].count++;
          }
        });

        // Trouver les textes correspondants aux choix sélectionnés
        const selectedTexts = selectedChoiceIds.map(selectedChoiceId => {
          // Trouver le texte correspondant au choix - essayer plusieurs méthodes
          let selectedOption = null;

          // Méthode 1: Chercher par ID exact
          selectedOption = questionOptions.find(option =>
            option.id !== undefined && option.id.toString() === selectedChoiceId
          );

          // Méthode 2: Si pas trouvé, chercher par index
          if (!selectedOption) {
            const indexAsNumber = parseInt(selectedChoiceId);
            if (!isNaN(indexAsNumber) && indexAsNumber >= 0 && indexAsNumber < questionOptions.length) {
              selectedOption = questionOptions[indexAsNumber];
            }
          }

          // Méthode 3: Chercher par index basé sur la position dans le tableau
          if (!selectedOption) {
            selectedOption = questionOptions.find((option, index) =>
              index.toString() === selectedChoiceId
            );
          }

          return selectedOption ? selectedOption.text : `Choix ${selectedChoiceId} (non trouvé)`;
        });

        // Obtenir le nom du promoteur selon le format disponible
        let promoterName;
        if (answer.PromoterName) {
          promoterName = answer.PromoterName;
        } else {
          promoterName = 'Promoteur inconnu';
        }

        tableData.push({
          id: `${answer.SurveySentId}-${questionId}`,
          choiceIds: selectedChoiceIds,
          choiceTexts: selectedTexts,
          choiceText: selectedTexts.join(', '), // Pour l'affichage dans le tableau
          promoter: promoterName,
          date: safeParseDate(answer.AnswerDate),
          isMultiple: isArray
        });
      }
    });

    const totalResponses = tableData.length;

    return { choicesCount, tableData, totalResponses, isMultipleChoice };
  }, [questionId, questionOptions, answers]);

  // Calcul du pourcentage et tri par nombre de réponses
  const chartData = useMemo(() => {
    const { choicesCount, totalResponses, isMultipleChoice } = choiceData;

    const choices = Object.values(choicesCount);

    if (isMultipleChoice) {
      // Pour choix multiple : utiliser l'option avec le plus de réponses comme référence 100%
      const maxCount = Math.max(...choices.map(choice => choice.count));

      return choices
        .map(choice => ({
          ...choice,
          percentage: maxCount > 0 ? ((choice.count / maxCount) * 100).toFixed(1) : 0
        }))
        .sort((a, b) => b.count - a.count);
    } else {
      // Pour choix unique : utiliser le pourcentage classique par rapport au total des réponses
      return choices
        .map(choice => ({
          ...choice,
          percentage: totalResponses > 0 ? ((choice.count / totalResponses) * 100).toFixed(1) : 0
        }))
        .sort((a, b) => b.count - a.count);
    }
  }, [choiceData]);

  // Définir les colonnes pour le tableau SortableTable
  const columns = [
    {
      key: 'promoter',
      label: 'Promoteur',
      render: (item) => item.promoter
    },
    {
      key: 'choiceText',
      label: 'Réponse',
      render: (item) => item.choiceText
    },
    {
      key: 'date',
      label: 'Date',
      render: (item) => item.date ? item.date.toLocaleDateString('fr-FR') : 'Date invalide'
    }
  ];

  // Couleurs pour l'histogramme
  const getBarColor = (index) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-purple-500',
      'bg-indigo-500',
      'bg-pink-500',
      'bg-gray-500'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <GIQuestionHeader
        questionId={questionId}
        questionText={questionText}
        questionType={choiceData.isMultipleChoice ? "Choix multiple" : "Choix unique"}
        badgeLabel={choiceData.isMultipleChoice ? "Multi" : "Choix"}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
      />

      {!isCollapsed && (
        <>
      {/* Tabs */}
      <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'summary'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            Résumé
          </button>
          <button
            onClick={() => setActiveTab('detail')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'detail'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            Détail
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'summary' ? (
          <div className="space-y-4">
            {/* Statistiques générales */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total des réponses: <span className="font-semibold text-gray-800 dark:text-gray-200">{choiceData.totalResponses}</span>
                {choiceData.isMultipleChoice && (
                  <span className="ml-3 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                    Choix multiple autorisé
                  </span>
                )}
              </div>
            </div>

            {/* Histogramme */}
            <div className="space-y-3">
              {chartData.map((choice, index) => (
                <div key={choice.id} className="space-y-1">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-700 dark:text-gray-300 font-medium truncate max-w-xs">
                      {choice.text}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                      {choice.count} {choiceData.isMultipleChoice ? `sélections (${choice.percentage}%)` : `(${choice.percentage}%)`}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 relative overflow-hidden">
                    <div
                      className={`h-4 rounded-full ${getBarColor(index)} transition-all duration-300`}
                      style={{
                        width: choiceData.isMultipleChoice
                          ? `${Math.min(100, (choice.count / Math.max(...chartData.map(c => c.count))) * 100)}%`
                          : `${choice.percentage}%`
                      }}
                    ></div>
                    {choice.count > 0 && (
                      <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white mix-blend-difference">
                        {choice.count}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {choiceData.totalResponses === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Aucune réponse pour cette question
              </div>
            )}
          </div>
        ) : (
          <div className="h-72">
            <SortableTable
              data={choiceData.tableData}
              columns={columns}
              emptyMessage="Aucune réponse pour cette question"
              exportable={true}
              exportFilename={`question-${questionId}-choix.csv`}
            />
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default GIChoiceQuestion;